/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import AsyncFeatureApp from './AsyncFeatureApp';
import type { CarlineAvailableStock, I18nMessages } from './ContentType';
import { mapHeadlessContent } from './utils/mapHeadlessContent';
import { createInitialState } from './utils/stateUtils';
import { FeatureAppContextProvider } from './context/FeatureAppContext';
import { darkTheme, lightTheme } from './theme';
import { getDesignLibrary } from '../alpha-helper';
import type {
  AsyncSsrManager,
  ContentService,
  GfaLocaleService,
  I18NService,
  OneGraphService,
  RenderModeService,
  SerializedStateManager,
  TrackingService,
} from './feature-service-types';

export interface Dependencies extends FeatureServices {
  readonly 's2:async-ssr-manager'?: AsyncSsrManager;
  readonly 's2:logger': Logger;
  readonly 'audi-content-service': ContentService;
  readonly 's2:serialized-state-manager'?: SerializedStateManager;
  readonly 'audi-tracking-service'?: TrackingService;
  readonly 'onegraph-service': OneGraphService;
  readonly 'gfa:locale-service': GfaLocaleService;
  readonly 'dbad:audi-i18n-service': I18NService;
  readonly 'audi-render-mode-service': RenderModeService;
}

export interface InitialState {
  readonly i18nMessages: I18nMessages;
  readonly carlineAvailableStock?: CarlineAvailableStock;
}

export type AsyncStateHolder = (() => Promise<InitialState>) | InitialState | undefined;

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, Dependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'onegraph-service': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '^5',
      ...getDesignLibrary({
        unifiedWebCommonVersion: '^1.13.0',
        unifiedWebAlphaCommonVersion: '^1.3.0',
      }),
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  create: ({ featureServices, featureAppId }: FeatureAppEnvironment<Dependencies, void>) => {
    const {
      'audi-tracking-service': trackingService,
      'gfa:locale-service': localeService,
      's2:logger': logger,
      'audi-content-service': contentService,
      's2:async-ssr-manager': asyncSsrManager,
      's2:serialized-state-manager': serializedStateManager,
      'onegraph-service': oneGraphService,
      'dbad:audi-i18n-service': i18nService,
      'audi-render-mode-service': renderModeService,
    } = featureServices;

    if (trackingService) {
      trackingService.featureAppName = 'oneaudi-feature-app-subpage-navigation';
    }

    const initialContent = contentService.getContent();
    logger?.info('Subpage navigation - initialContent: ', initialContent);

    const content = mapHeadlessContent(initialContent);
    logger?.info('Subpage navigation - content: ', content);

    const theme = process.env.IS_LEGACY ? lightTheme : darkTheme; // theme on Subemu, this light theme necessary for main menu

    let asyncStateHolder: AsyncStateHolder;
    if (asyncSsrManager) {
      // on the server
      asyncSsrManager.scheduleRerender(
        (async () => {
          asyncStateHolder = await createInitialState(
            oneGraphService,
            localeService,
            content,
            i18nService,
            logger,
          );
          serializedStateManager?.register(() => JSON.stringify(asyncStateHolder));
        })(),
      );
    } else {
      // on the client
      const serializedState = serializedStateManager?.getSerializedState();
      if (serializedState) {
        asyncStateHolder = JSON.parse(serializedState) as InitialState;
      } else {
        logger?.warn(
          `Serialized state not found!. Possible reasons: \n 1. Running app in dev mode using the "audi-static-demo-integrator" which does not support SSR \n 2. Running app in prod mode and SSR is broken`,
        );
        asyncStateHolder = () =>
          createInitialState(oneGraphService, localeService, content, i18nService, logger);
      }
    }

    return {
      render: () => {
        return (
          <ThemeProvider theme={theme}>
            <FeatureAppContextProvider
              featureAppId={featureAppId}
              logger={logger}
              trackingService={trackingService}
              contentService={contentService}
              renderModeService={renderModeService}
              localeService={localeService}
              i18nService={i18nService}
              oneGraphService={oneGraphService}
            >
              <AsyncFeatureApp asyncStateHolder={asyncStateHolder} />
            </FeatureAppContextProvider>
          </ThemeProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
